import * as THREE from 'three';
import type { TrackSample } from '@/world/Track';
import type { LandmarkKind } from '@/config/cities';
import { forwardX, forwardZ, headingFromForward } from '@/math/orientation';
import type { LandmarkDef } from '@/world/landmarks/common';
import { NYC_LANDMARKS } from '@/world/landmarks/nyc';
import { LONDON_LANDMARKS } from '@/world/landmarks/london';
import { NAPOLI_LANDMARKS } from '@/world/landmarks/napoli';
import { TOKYO_LANDMARKS } from '@/world/landmarks/tokyo';
import { RIO_LANDMARKS } from '@/world/landmarks/rio';
import { DAKAR_LANDMARKS } from '@/world/landmarks/dakar';
import { LAGOS_LANDMARKS } from '@/world/landmarks/lagos';
import { MUMBAI_LANDMARKS } from '@/world/landmarks/mumbai';
import { PATAGONIA_LANDMARKS } from '@/world/landmarks/patagonia';

/**
 * Landmarks.ts — big, recognizable low-poly hero models dropped along a city's
 * route. The models live in per-city modules (src/world/landmarks/<city>.ts);
 * this file is the registry + the placement logic. Each landmark declares HOW
 * it sits (see LandmarkDef):
 *
 *   'side'   — beside the road, `offset` beyond the guardrail, facing the road
 *              (side 0 → on the centreline far ahead, facing oncoming traffic);
 *   'span'   — centred ON the road, aligned to travel (drive-through gateways
 *              and overpasses — Bow Bridge, Tower Bridge);
 *   'finish' — centred `ahead` past the COURSE END (the last road node, just
 *              inside the end barrier), facing back at the racers — the stage
 *              backdrop behind the run-out (the NYE tower, the Maracanã).
 *
 * All models are built facing local +Z and are swappable for the Design pass.
 */

const REGISTRY: Record<LandmarkKind, LandmarkDef> = {
  ...NYC_LANDMARKS,
  ...LONDON_LANDMARKS,
  ...NAPOLI_LANDMARKS,
  ...TOKYO_LANDMARKS,
  ...RIO_LANDMARKS,
  ...DAKAR_LANDMARKS,
  ...LAGOS_LANDMARKS,
  ...MUMBAI_LANDMARKS,
  ...PATAGONIA_LANDMARKS,
} as Record<LandmarkKind, LandmarkDef>;

/** Build a landmark and place + orient it relative to a track sample.
 *  `endSample` is the COURSE END (the last road node) — 'finish' landmarks
 *  anchor from there so they stand beyond the end barrier, framing the
 *  run-out instead of blocking it (M97: cars used to coast straight through
 *  the NYE tower / into the Maracanã wall). */
export function buildLandmark(
  kind: LandmarkKind,
  sample: TrackSample,
  side: -1 | 0 | 1,
  halfWidth: number,
  endSample?: TrackSample,
): THREE.Group {
  const def = REGISTRY[kind];
  const g = def.build(halfWidth);

  if (def.placement === 'span') {
    g.position.set(sample.x, sample.y, sample.z);
    g.rotation.y = headingFromForward(forwardX(sample.dir), forwardZ(sample.dir));
    return g;
  }

  if (def.placement === 'finish') {
    const anchor = endSample ?? sample;
    const ahead = def.ahead ?? 30;
    g.position.set(
      anchor.x + forwardX(anchor.dir) * ahead,
      anchor.y,
      anchor.z + forwardZ(anchor.dir) * ahead,
    );
    g.rotation.y = headingFromForward(-forwardX(anchor.dir), -forwardZ(anchor.dir));
    return g;
  }

  const lateral = halfWidth + (def.offset ?? 10);
  g.position.set(
    sample.x + sample.rightX * side * lateral,
    sample.y,
    sample.z + sample.rightZ * side * lateral,
  );
  const fx = side === 0 ? -forwardX(sample.dir) : -side * sample.rightX;
  const fz = side === 0 ? -forwardZ(sample.dir) : -side * sample.rightZ;
  g.rotation.y = headingFromForward(fx, fz);
  return g;
}
